import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Camera, Barcode, Check, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import BarcodeScanner from './BarcodeScanner';

// Green theme tokens
const THEME = {
  primary: '#86EFAC',
  light: '#DCFCE7',
  lighter: '#F0FDF4',
  dark: '#166534',
  icon: '#22C55E',
};

const AddFavoriteDialog = ({ open, onOpenChange, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: 'other',
    store: '',
    identification_notes: '',
    barcode: '',
    link_url: '',
    typical_price: '',
    size_variant: '',
    who_likes: '',
    why_like: '',
    image_url: '',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      category: 'other',
      store: '',
      identification_notes: '',
      barcode: '',
      link_url: '',
      typical_price: '',
      size_variant: '',
      who_likes: '',
      why_like: '',
      image_url: '',
    });
    setLookupResult(null);
  };

  const handleBarcodeScan = async (barcode, format) => {
    setShowScanner(false);
    setFormData(prev => ({ ...prev, barcode }));
    
    // Look up product info
    setLookingUp(true);
    try {
      const response = await API.get(`/shopping/barcode/lookup/${barcode}`);
      
      if (response.data.found) {
        const product = response.data.product;
        setLookupResult({ found: true, source: response.data.source });
        
        // Auto-populate form with found data (user can override)
        setFormData(prev => ({
          ...prev,
          barcode,
          name: product.name || prev.name,
          brand: product.brand || prev.brand,
          category: product.category || prev.category,
          size_variant: product.quantity || product.size_variant || prev.size_variant,
          image_url: product.image_url || prev.image_url,
          store: product.stores || prev.store,
        }));
        
        toast.success(`Found: ${product.name || 'Product'}`, {
          description: 'Product info loaded. You can edit any field.',
        });
      } else {
        setLookupResult({ found: false });
        toast.info('Product not in database', {
          description: 'Please enter the details manually.',
        });
      }
    } catch (error) {
      console.error('Barcode lookup failed:', error);
      setLookupResult({ found: false, error: true });
      toast.error('Failed to look up barcode');
    } finally {
      setLookingUp(false);
    }
  };

  const handleManualBarcodeLookup = async () => {
    const barcode = formData.barcode.trim();
    if (!barcode) {
      toast.error('Please enter a barcode first');
      return;
    }
    await handleBarcodeScan(barcode, 'manual');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter a product name');
      return;
    }
    
    setLoading(true);
    try {
      await API.post('/shopping/favorites', {
        name: formData.name.trim(),
        brand: formData.brand.trim() || null,
        category: formData.category,
        store: formData.store.trim() || null,
        identification_notes: formData.identification_notes.trim() || null,
        barcode: formData.barcode.trim() || null,
        link_url: formData.link_url.trim() || null,
        typical_price: formData.typical_price ? parseFloat(formData.typical_price) : null,
        size_variant: formData.size_variant.trim() || null,
        who_likes: formData.who_likes.trim() || null,
        why_like: formData.why_like.trim() || null,
      });
      toast.success('Favorite saved!');
      resetForm();
      onSuccess();
    } catch (error) {
      console.error('Failed to save favorite:', error);
      toast.error('Failed to save favorite');
    } finally {
      setLoading(false);
    }
  };

  // Show barcode scanner
  if (showScanner) {
    return (
      <BarcodeScanner
        onScan={handleBarcodeScan}
        onClose={() => setShowScanner(false)}
        theme={THEME}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Save a Favorite Product</DialogTitle>
          <DialogDescription>
            Save products you love so you can find them again
          </DialogDescription>
        </DialogHeader>
        
        {/* Barcode Scanner Card */}
        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow"
          style={{ backgroundColor: THEME.lighter, borderColor: THEME.light }}
          onClick={() => setShowScanner(true)}
          data-testid="scan-barcode-card"
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div 
                className="h-12 w-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: THEME.light }}
              >
                <Camera className="h-6 w-6" style={{ color: THEME.dark }} />
              </div>
              <div className="flex-1">
                <div className="font-medium" style={{ color: THEME.dark }}>
                  Scan Barcode
                </div>
                <div className="text-sm text-gray-500">
                  Auto-fill product info by scanning
                </div>
              </div>
              <Barcode className="h-5 w-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        
        {/* Lookup Result Indicator */}
        {lookupResult && (
          <div 
            className="flex items-center gap-2 p-2 rounded-lg text-sm"
            style={{ 
              backgroundColor: lookupResult.found ? THEME.lighter : '#FEF3C7',
              color: lookupResult.found ? THEME.dark : '#92400E'
            }}
          >
            {lookupResult.found ? (
              <>
                <Check className="h-4 w-4" />
                <span>Product info loaded from {lookupResult.source}</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4" />
                <span>Not found - please fill in manually</span>
              </>
            )}
          </div>
        )}
        
        {/* Product Image Preview */}
        {formData.image_url && (
          <div className="flex justify-center">
            <img 
              src={formData.image_url} 
              alt="Product" 
              className="h-24 w-24 object-contain rounded-lg border"
            />
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Core */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-2">
              <Label htmlFor="name">Product Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="What is this product?"
                data-testid="input-favorite-name"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="brand">Brand</Label>
              <Input
                id="brand"
                value={formData.brand}
                onChange={(e) => handleChange('brand', e.target.value)}
                placeholder="e.g., Mrs. Meyers"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="food">Food</SelectItem>
                  <SelectItem value="household">Household</SelectItem>
                  <SelectItem value="personal_care">Personal Care</SelectItem>
                  <SelectItem value="pet">Pet</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Identification */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">How to Identify It</h4>
            
            <div className="space-y-2">
              <Label htmlFor="identification_notes">Description / Notes</Label>
              <Textarea
                id="identification_notes"
                value={formData.identification_notes}
                onChange={(e) => handleChange('identification_notes', e.target.value)}
                placeholder="e.g., Green bottle, lavender scent, blue lid"
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="store">Where to Buy</Label>
                <Input
                  id="store"
                  value={formData.store}
                  onChange={(e) => handleChange('store', e.target.value)}
                  placeholder="e.g., Target, Costco"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="barcode">Barcode</Label>
                <div className="flex gap-2">
                  <Input
                    id="barcode"
                    value={formData.barcode}
                    onChange={(e) => handleChange('barcode', e.target.value)}
                    placeholder="Enter or scan"
                    data-testid="input-barcode"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleManualBarcodeLookup}
                    disabled={lookingUp || !formData.barcode}
                    title="Look up barcode"
                    data-testid="lookup-barcode-btn"
                  >
                    {lookingUp ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Barcode className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Buy-again info */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Buy-Again Info</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="typical_price">Typical Price ($)</Label>
                <Input
                  id="typical_price"
                  type="number"
                  step="0.01"
                  value={formData.typical_price}
                  onChange={(e) => handleChange('typical_price', e.target.value)}
                  placeholder="0.00"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="size_variant">Size / Variant</Label>
                <Input
                  id="size_variant"
                  value={formData.size_variant}
                  onChange={(e) => handleChange('size_variant', e.target.value)}
                  placeholder="e.g., 16 oz, Large"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="link_url">Link (URL)</Label>
              <Input
                id="link_url"
                value={formData.link_url}
                onChange={(e) => handleChange('link_url', e.target.value)}
                placeholder="https://..."
              />
            </div>
          </div>
          
          {/* Context */}
          <div className="border-t pt-4 space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Context</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="who_likes">Who Likes It</Label>
                <Input
                  id="who_likes"
                  value={formData.who_likes}
                  onChange={(e) => handleChange('who_likes', e.target.value)}
                  placeholder="e.g., Me, Kids, Dog"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="why_like">Why You Like It</Label>
                <Input
                  id="why_like"
                  value={formData.why_like}
                  onChange={(e) => handleChange('why_like', e.target.value)}
                  placeholder="e.g., Best scent ever"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              style={{ backgroundColor: THEME.primary, color: THEME.dark }}
              data-testid="submit-favorite-btn"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Favorite
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddFavoriteDialog;
